import type {
  BillingInfo,
  BillingUpdateCardRequest,
  ServicesClientsOrdwayBillingTotals,
} from '@customerio/hydra-ui-api';
import type { BillingVendor } from '@/services/auth/types';
import type { FeatureGroup } from './feature-groups';
import type { PlanType } from './plans';

/**
 * The confirmed Stripe SetupIntent, as the billing API publishes it. Derived from the request
 * body rather than from a named Stripe schema: the API publishes this field opaquely, so no
 * Stripe component name is guaranteed to exist. Both billing endpoints that accept a
 * SetupIntent carry the same schema for it, so one alias covers both.
 */
export type SetupIntentPayload = NonNullable<BillingUpdateCardRequest['setup_intent']>;

/**
 * Shared billing-plan types ported from the Ember UI (`app/utils/billing/index.ts`).
 *
 * In Ember, three Ember Data models (`plan`, `ordway-subscription`, `ordway-plan`)
 * each implemented {@link BillingPlan} via getters. In Hydra the same shape is
 * produced by pure transform functions (see `./transforms`) that run once when the
 * billing service loads data, so there are no per-instance getters here.
 */

/**
 * Which billing system a plan or subscription belongs to. Added to the transform outputs as a
 * discriminant so consumers can tell the shapes apart with a simple
 * `x.billingSystem === BillingSystem.Ordway` check (rather than structural sniffing). Values
 * match the account model's `billing_vendor` so the two are directly comparable — the
 * `satisfies BillingVendor` check below enforces that rather than leaving it to a comment.
 */
export const BillingSystem = {
  Stripe: 'legacy_stripe',
  Ordway: 'ordway',
  Metronome: 'metronome',
} as const satisfies Record<string, BillingVendor>;
export type BillingSystem = (typeof BillingSystem)[keyof typeof BillingSystem];

/** The billing totals for an Ordway subscription. */
export type BillingTotals = ServicesClientsOrdwayBillingTotals;

/**
 * Billing overage estimates. The base fields come from the generated `billing/info` estimate model
 * (`services_ui_api_model_BillingEstimates`). `mau_overage` is optional because only the Ordway
 * `billing/overage_estimate` endpoint produces it — the Stripe `billing_estimates` never does — and
 * consumers read it with a `?? 0` fallback.
 */
export type BillingEstimates = BillingInfo['billing_estimates'] & { mau_overage?: number };

/**
 * Vendor-neutral saved-card info. Each billing vendor (Stripe, Ordway, and the incoming third
 * Stripe-based platform) maps its raw card model into this shape via a transformer in its vendor
 * folder (`stripe/card.ts`, `ordway/card.ts`), mirroring how {@link BillingPlan} unifies plans.
 * Values are normalized (lowercased brand, last-4 already sliced); display fallbacks (masking,
 * placeholders) belong in the consuming component, not here.
 */
export interface PaymentCard {
  /** Lowercased card brand, e.g. `visa` / `mastercard` / `amex` / `discover`; empty when unknown. */
  brand: string;
  /** Last four digits of the card; empty when unknown. */
  last4: string;
  /** Expiry as reported by the vendor (format varies); empty when unknown. */
  expiry: string;
  /** Cardholder name; empty when unknown. */
  ownerName: string;
}

type HighlightRoute = { label: string; route: string; query?: Record<string, string> };
type HighlightLink = { label: string; href: string };
export type Highlight = string | HighlightLink | HighlightRoute;

export interface BillingPlan {
  /**
   * The price of the plan without tax, in cents (i.e. dollars * 100) as an int
   */
  basePrice: number;
  /**
   * The display value of the recurring billing period. e.g. "Monthly", "Yearly"
   */
  intervalDisplayName: string;
  /**
   * The amount of customers included in the plan as an int
   */
  includedCustomers: number;
  /**
   * The amount of profiles included in the plan as an int
   */
  includedProfiles: number;
  /**
   * The amount of objects included in the plan as an int
   */
  includedObjects: number;
  /**
   * The amount of messages included in the plan as an int
   */
  includedMessages: number;
  /**
   * The amount of monthly active users included in the plan as an int
   */
  includedMau?: number;
  /**
   * The rate for each additional customer in the account, as a string formatted
   * as dollars. e.g. "0.0002" for 2 hundredths of a dollar for each additional
   * customer in the account
   */
  customerOverageRate: string;
  /**
   * The rate for each additional profile in the account, as a string formatted
   * as dollars. e.g. "0.0002" for 2 hundredths of a dollar for each additional
   * profile in the account
   */
  profileOverageRate: string;
  /**
   * The rate for each additional object in the account, as a string formatted
   * as dollars. e.g. "0.0002" for 2 hundredths of a dollar for each additional
   * object in the account
   */
  objectOverageRate: string;
  /**
   * The rate for each additional message in the account, as a string formatted
   * as dollars. e.g. "0.0002" for 2 hundredths of a dollar for each additional
   * message in the account
   */
  messageOverageRate: string;
  /**
   * The rate for each additional monthly active user in the account, as a string formatted
   * as dollars. e.g. "0.0002" for 2 hundredths of a dollar for each additional
   * monthly active user (?) in the account
   */
  mauOverageRate?: string;
  /**
   * Whether the plan can be cancelled by the user or not
   */
  canCancel: boolean;
  /**
   * The type of plan
   */
  planType: PlanType;
  /**
   * The feature groups that the plan belongs to. Can be more than one.
   */
  featureGroups: FeatureGroup[];
  /**
   * A name for the plan
   */
  name: string;
  /**
   * A short description about the base features of the plan
   */
  description?: string;
  /**
   * Deprecated field for CDP premium
   */
  cdpPremiumPrice?: number;
  /**
   * Whether the plan is paid annually or not
   */
  isPaidAnnually: boolean;
  /**
   * The price when paying annually
   */
  annualPrice: number;
  /**
   * Specific features of the plan to be displayed in a list to compare plans.
   * Can contain HTML.
   */
  highlights: Highlight[];
  /**
   * Whether the plan is a trial. Can be either an essentials or premium trial.
   */
  isTrial: boolean;
  /**
   * Whether the plan is MAU pricing or not. Only applicable for Ordway billing.
   */
  isMauPricing: boolean;
  /**
   * The billing totals for the plan, which includes the due today amount, due today
   * pre-savings amount, and due today savings amount. Only available for Ordway subscriptions.
   */
  billingTotals?: BillingTotals;
}
